import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HelpTip from '../../components/HelpTip';
import PermissionLink from '../../components/PermissionLink';
import SearchSelect from '../../components/SearchSelect';
import {
    DeviceDetail,
    DeviceThermalCal,
    deleteDevice,
    fetchDeviceDeletePreview,
    getDevice,
    updateDevice,
    publishDevice,
} from '../../api/device';
import {
    PublishBadge,
    PublishToggle,
    isPublishedValue,
} from '../../components/PublishBadge';
import DeleteCascadeModal from '../../components/DeleteCascadeModal';
import { Tool, listTools } from '../../api/toolbox';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { actions, useDeviceCtx, useCan } from '../../store';
import { useDispatch } from 'react-redux';
import DeviceTopSelector from './DeviceTopSelector';
import YamlImportModal from './YamlImportModal';
import DeviceCreateModal from './DeviceCreateModal';
import DeviceSchemaGroups from './DeviceSchemaGroups';
import DeviceCapabilityCard from './DeviceCapabilityCard';
import DeviceTipsCard from './DeviceTipsCard';
import DeviceCapTypesCard from './DeviceCapTypesCard';
import MModeRefModal from './MModeRefModal';
import {
    MMDeviceRef,
    MMMode,
    MMGroup,
    listMMDeviceRefs,
    listMMGroups,
    loadMModesCached,
} from '../../api/mmode';
import { alertDialog } from '../../utils/dialog';

// 设备工况 · 设备树/工况 子页. 与原型 devProfileView() 对齐:
// - 顶部统一设备选择 (跨子页共享, 通过 store)
// - 设备能力 卡片 (基础参数 10 行)
// - 模式能力开关 (line/fill/bitmap/cut · 启用|禁用 + speed 范围)
// - 热校准 (mode × 材料类型 → τ/β, 用于物理散热模型)
// - 功率校准曲线 (SVG)
// 列表式被废弃: 浏览全部设备改在顶部 select 一站搞定.

function CalibChart({
    points,
    stdW,
}: {
    points: { pct: number; realW: number }[];
    stdW: number;
}) {
    if (points.length === 0) {
        return <div className="text-xs text-slate-400">无校准点</div>;
    }
    const W = 520;
    const H = 160;
    const pad = 28;
    const stdWVal = Math.max(stdW || 1, ...points.map((p) => p.realW));
    const x = (pct: number) => pad + (pct / 100) * (W - 2 * pad);
    const y = (w: number) => H - pad - (w / stdWVal) * (H - 2 * pad);
    const sorted = [...points].sort((a, b) => a.pct - b.pct);
    const path = sorted
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.pct)} ${y(p.realW)}`)
        .join(' ');
    return (
        <svg width={W} height={H} className="block max-w-full">
            {[0, 25, 50, 75, 100].map((g) => (
                <line
                    key={g}
                    x1={x(g)}
                    y1={pad}
                    x2={x(g)}
                    y2={H - pad}
                    stroke="#f1f5f9"
                />
            ))}
            <line
                x1={pad}
                y1={H - pad}
                x2={W - pad}
                y2={H - pad}
                stroke="#cbd5e1"
            />
            <line x1={pad} y1={pad} x2={pad} y2={H - pad} stroke="#cbd5e1" />
            <path d={path} fill="none" stroke="#3b6cf6" strokeWidth={2} />
            {sorted.map((p) => (
                <circle
                    key={p.pct}
                    cx={x(p.pct)}
                    cy={y(p.realW)}
                    r={2.5}
                    fill="#3b6cf6"
                />
            ))}
            {[0, 25, 50, 75, 100].map((g) => (
                <text
                    key={g}
                    x={x(g)}
                    y={H - 10}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#94a3b8"
                >
                    {g}%
                </text>
            ))}
            <text
                x={W - pad}
                y={H - pad - 4}
                textAnchor="end"
                fontSize="10"
                fill="#94a3b8"
            >
                软件功率%
            </text>
            <text x={pad + 4} y={pad + 10} fontSize="10" fill="#94a3b8">
                真实功率 W
            </text>
        </svg>
    );
}

function CapRow({
    label,
    value,
    hint,
}: {
    label: string;
    value: React.ReactNode;
    hint?: string;
}) {
    return (
        <div className="flex items-baseline justify-between text-sm py-1.5 border-b border-slate-50 last:border-b-0">
            <span className="text-slate-500">{label}</span>
            <span className="font-mono text-slate-700 text-right">
                {value || '—'}{' '}
                {hint && (
                    <span className="text-xs text-slate-400 ml-1">{hint}</span>
                )}
            </span>
        </div>
    );
}

function ThermalCalTable({ rows }: { rows: DeviceThermalCal[] }) {
    if (rows.length === 0) {
        return (
            <div className="text-xs text-slate-400">
                该设备暂无热校准数据 (τ/β 用于材料散热模型, 影响参数推荐; 可通过
                YAML 导入补充)
            </div>
        );
    }
    return (
        <table className="w-full text-sm">
            <thead className="bg-slate-50">
                <tr>
                    <th className="th">模式</th>
                    <th className="th">材料类型</th>
                    <th className="th">τ (tau)</th>
                    <th className="th">β (beta)</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((t, i) => (
                    <tr key={`${t.deviceKey}-${t.mode}-${t.materialType}-${i}`}>
                        <td className="td">{t.mode}</td>
                        <td className="td">{t.materialType}</td>
                        <td className="td font-mono">{t.tau ?? '—'}</td>
                        <td className="td font-mono">{t.beta ?? '—'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// ConvertToolBinding 设备级换算服务绑定 (device.convert_tool_key 单列):
// 顶栏就地查看/改绑, 候选为工具市场 live 服务; 统一走审批工单.
// 可换算范围由规则决定 (同工艺架构 + 同光源), 不再逐对登记.
function ConvertToolBinding({
    detail,
    onSaved,
}: {
    detail: DeviceDetail;
    onSaved: () => void;
}) {
    const canUpdate = useCan('device', 'update');
    const [tools, setTools] = useState<Tool[] | null>(null);
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(detail.convertToolKey || '');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setVal(detail.convertToolKey || '');
        setEditing(false);
    }, [detail.deviceKey, detail.convertToolKey]);

    useEffect(() => {
        if (!editing || tools !== null) return;
        listTools({ status: 'live', pageSize: 100 })
            .then((r) => setTools(r.list || []))
            .catch(() => setTools([]));
    }, [editing, tools]);

    const save = async () => {
        setSaving(true);
        try {
            const res = await updateDevice(detail.deviceKey, {
                convertToolKey: val,
            });
            if (!alertApprovalIfPending(res.message)) onSaved();
            setEditing(false);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog(`绑定失败: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    // 换算服务绑定是设备管理动作: 无 device:update 权的人整块不显示 (含说明气泡), 2026-09-12
    if (!canUpdate) return null;
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500 whitespace-nowrap">
                换算服务
            </span>
            {editing ? (
                <>
                    <SearchSelect
                        className="w-64 text-sm"
                        value={val}
                        onChange={(v) => setVal(v)}
                        disabled={saving}
                        placeholder="未绑定"
                        options={(tools || []).map((t) => ({
                            value: t.toolKey,
                            label: `${t.name} (${t.toolKey})`,
                        }))}
                        clearable
                    />
                    <button
                        type="button"
                        className="btn-primary text-xs px-2 py-1"
                        onClick={save}
                        disabled={saving}
                    >
                        {saving ? '保存中…' : '保存'}
                    </button>
                    <button
                        type="button"
                        className="btn-ghost text-xs px-2 py-1"
                        onClick={() => {
                            setVal(detail.convertToolKey || '');
                            setEditing(false);
                        }}
                        disabled={saving}
                    >
                        取消
                    </button>
                </>
            ) : (
                <>
                    {detail.convertToolKey ? (
                        <Link
                            to={`/tools/${encodeURIComponent(detail.convertToolKey)}`}
                            className="tag bg-brand-50 text-brand-700 hover:underline"
                        >
                            {detail.convertToolKey}
                        </Link>
                    ) : (
                        <span className="tag bg-slate-100 text-slate-500">
                            未绑定
                        </span>
                    )}
                    <button
                        type="button"
                        className="text-xs text-brand-600 hover:underline whitespace-nowrap"
                        onClick={() => setEditing(true)}
                        hidden={!canUpdate}
                    >
                        改绑 →
                    </button>
                </>
            )}
            <HelpTip text="设备绑定一个换算服务 (工具市场 live 服务, 经统一 Tool API 调用)。可换算的设备范围按规则判定: 工艺架构 + 光源相同即可互相换算, 无需逐对登记。" />
        </div>
    );
}

function DeviceProfile() {
    const canUpdate = useCan('device', 'update');
    const canDelete = useCan('device', 'delete');
    // 跨域板块按各自资源查看权渲染 (2026-09-12): 换算服务是 tool 域 (挂载的小贴士随设备数据显示)
    const canSeeTool = useCan('tool', 'get');
    const ctx = useDeviceCtx();
    const dispatch = useDispatch();
    const [detail, setDetail] = useState<DeviceDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showImport, setShowImport] = useState(false);
    // 编辑设备弹窗 (2026-09-17 起本页只做编辑): 新建走 /devices/new 创建向导,
    // 入口统一收在设备管理页 —— 工况页是"看某台设备"的地方, 在这里建新设备等于换了上下文.
    const [showCreate, setShowCreate] = useState(false);
    // 删除确认弹窗 (2026-08-21 硬删 + 关联数据预检勾选)
    const [showDelete, setShowDelete] = useState(false);
    const [editingDevice, setEditingDevice] = useState<DeviceDetail | null>(
        null,
    );
    // ★设计A: 本设备的加工模式引用 (mm_device_mode) — 工况页只读展示, 维护复用设备管理的弹窗
    const [mmRefs, setMmRefs] = useState<MMDeviceRef[]>([]);
    const [mmModes, setMmModes] = useState<MMMode[]>([]);
    const [mmGroups, setMmGroups] = useState<MMGroup[]>([]);
    const [mmEditOpen, setMmEditOpen] = useState(false);
    useEffect(() => {
        loadMModesCached().then(setMmModes);
        listMMGroups()
            .then(setMmGroups)
            .catch(() => setMmGroups([]));
    }, []);
    const reloadMmRefs = React.useCallback(() => {
        if (!ctx.curDeviceKey) {
            setMmRefs([]);
            return;
        }
        listMMDeviceRefs(ctx.curDeviceKey)
            .then(setMmRefs)
            .catch(() => setMmRefs([]));
    }, [ctx.curDeviceKey]);
    useEffect(reloadMmRefs, [reloadMmRefs]);
    // 引用项显示名: 设备别名 > 模式值中文 > 原值; 括号带归属组名 (与模板键同一套叫法)
    const mmRefLabel = (r: MMDeviceRef): string => {
        const mode = mmModes.find((m) => m.value === r.modeValue);
        const name = r.aliasZh || mode?.nameZh || r.modeValue;
        const groupZh = mmGroups.find(
            (g) => g.code === mode?.groupCode,
        )?.nameZh;
        return groupZh && groupZh !== name ? `${name} · ${groupZh}` : name;
    };

    const reload = React.useCallback(() => {
        if (!ctx.curDeviceKey) {
            setDetail(null);
            return;
        }
        setLoading(true);
        getDevice(ctx.curDeviceKey)
            .then((d) => {
                setDetail(d);
                setError('');
            })
            .catch((e) => {
                const msg =
                    e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e);
                setError(msg);
                setDetail(null);
            })
            .finally(() => setLoading(false));
    }, [ctx.curDeviceKey]);

    useEffect(() => {
        reload();
    }, [reload]);

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="text-base font-semibold">
                        设备工况 Profile
                    </div>
                    <HelpTip text="分层合并: family_defaults → shared_models → selected_device → power_calibration。能力字段由设备绑定的框架模板 (Schema) 定义, 元数据固定行仅 名称/大类/工艺架构/状态。" />
                </div>
                <div className="flex gap-2">
                    {/* 删除 / 编辑 / 新增: 登录即可操作; 提交后走审批工单 */}
                    {detail && (
                        <>
                            <button
                                type="button"
                                className="text-sm px-3 py-1.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50"
                                onClick={() => setShowDelete(true)}
                                hidden={!canDelete}
                            >
                                ✕ 删除当前设备
                            </button>
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => {
                                    setEditingDevice(detail);
                                    setShowCreate(true);
                                }}
                                hidden={!canUpdate}
                            >
                                ✎ 编辑设备
                            </button>
                        </>
                    )}
                    {/* YAML 导入对所有登录用户开放: 普通用户仅能新增 (后端 add-only 模式 — 设备已存在直接报错) */}
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setShowImport(true)}
                        hidden={!canUpdate}
                    >
                        ⬆ 导入 YAML
                    </button>
                </div>
            </div>

            <div className="card p-4 flex items-center justify-between gap-4 flex-wrap">
                <DeviceTopSelector />
                {detail && canSeeTool && (
                    <ConvertToolBinding detail={detail} onSaved={reload} />
                )}
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-center text-slate-400 text-sm py-10">
                    加载中…
                </div>
            ) : !detail ? (
                <div className="text-slate-400 text-sm text-center py-10">
                    请选择设备
                </div>
            ) : (
                <div className="grid grid-cols-3 gap-4">
                    {/* 设备能力: 身份行固定; 能力字段定义来自 Schema (device_schema · capability),
                        值统一存 device_ext_value (2026-08-14 store=column 机制移除) */}
                    <div className="card p-4 col-span-1">
                        <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                            设备能力
                            <span className="tag bg-brand-50 text-brand-700 font-normal text-[10px]">
                                Schema 驱动
                            </span>
                            <PermissionLink
                                resource="device_schema"
                                verb="update"
                                hide
                                to={`/devices/templates?focus=${encodeURIComponent(detail.archCode)}`}
                                className="tag bg-slate-100 text-slate-500 font-normal text-[10px] hover:bg-brand-50 hover:text-brand-600"
                                title="本设备绑定的字段模板 (一个工艺框架一个模板), 表单按模板渲染 · 点击查看/管理模板"
                            >
                                引用模板: {detail.archCode} ↗
                            </PermissionLink>
                        </div>
                        {/* 元数据固定行仅 4 项 (device_key 在顶部选择器): 其余能力字段全部 Schema 驱动、按框架实例化 */}
                        <CapRow label="设备名称" value={detail.name || '—'} />
                        {/* device_key: 参数导入模板的 deviceKey 列填的就是它 */}
                        <CapRow
                            label="设备标识"
                            value={detail.deviceKey}
                            hint="device_key"
                        />
                        <CapRow label="设备大类" value={detail.family} />
                        <CapRow label="工艺架构" value={detail.archCode} />
                        <CapRow
                            label="状态"
                            value={
                                detail.status === 'enabled' ? '启用' : '停用'
                            }
                        />
                        <CapRow
                            label="发布"
                            value={
                                <span className="inline-flex items-center gap-2 font-sans">
                                    <PublishBadge
                                        isPublished={detail.isPublished}
                                        publishedAt={detail.publishedAt}
                                        layout="inline"
                                    />
                                    <PublishToggle
                                        resource="device"
                                        published={isPublishedValue(
                                            detail.isPublished,
                                        )}
                                        label={detail.name || detail.deviceKey}
                                        onToggle={(p) =>
                                            publishDevice(detail.deviceKey, p)
                                        }
                                        onDone={reload}
                                        className="text-xs text-brand-600 hover:underline"
                                    />
                                </span>
                            }
                        />
                        <DeviceCapabilityCard
                            detail={detail}
                            onChanged={reload}
                        />
                    </div>

                    {/* 矩阵字段组 (激光模式能力 / 场镜光斑 / FDM 工艺预设等, 各组自带标题; 支持夹具随 capability 组在左侧设备能力卡内渲染).
                        卡片标题 2026-10-24 前写死「模式能力开关」, FDM 工艺预设也挂在这个标题下 */}
                    <div className="card p-4 col-span-2 overflow-x-auto">
                        <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                            矩阵字段组
                            <span className="tag bg-brand-50 text-brand-700 font-normal text-[10px]">
                                Schema 驱动
                            </span>
                        </div>
                        <DeviceSchemaGroups
                            deviceKey={detail.deviceKey}
                            archCode={detail.archCode}
                            layoutFilter="matrix"
                            groups={detail.schemaGroups}
                            extValues={detail.extValues}
                            onReload={reload}
                        />
                    </div>

                    {/* ★设计A: 加工模式引用 — 该设备勾选支持的模式 (设备管理弹窗维护的那份数据), 工况页可见可维护 */}
                    <div className="card p-4 col-span-3">
                        <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                            加工模式引用
                            <span className="text-[11px] text-slate-400 font-normal">
                                该设备支持的加工模式 (决定参数录入的模式候选) ·
                                与「矩阵字段组」里的模式能力是不同维度
                            </span>
                            <button
                                type="button"
                                className="ml-auto text-xs text-brand-600 hover:underline"
                                onClick={() => setMmEditOpen(true)}
                                hidden={!canUpdate}
                            >
                                ✎ 维护
                            </button>
                        </div>
                        {mmRefs.length === 0 ? (
                            <div className="text-xs text-slate-400">
                                未勾选任何加工模式 —
                                参数录入时该设备的模式候选会回退全量字典;
                                点右上「维护」勾选
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {mmRefs.map((r) => (
                                    <span
                                        key={r.modeValue}
                                        className={`tag ${
                                            r.enabled
                                                ? 'bg-brand-50 text-brand-700'
                                                : 'bg-slate-100 text-slate-400 line-through'
                                        }`}
                                        title={`${r.modeValue}${
                                            r.speedMin != null ||
                                            r.speedMax != null
                                                ? ` · 速度 ${r.speedMin ?? '?'}–${r.speedMax ?? '?'}`
                                                : ''
                                        }${r.enabled ? '' : ' · 已停用'}`}
                                    >
                                        {mmRefLabel(r)}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 自定义平铺字段组: 模板页新建的非 capability 平铺组在此承载 (无匹配组时整块隐藏) */}
                    <DeviceSchemaGroups
                        deviceKey={detail.deviceKey}
                        archCode={detail.archCode}
                        layoutFilter="flat"
                        exclude={['capability']}
                        groups={detail.schemaGroups}
                        extValues={detail.extValues}
                        onReload={reload}
                        hideEmpty
                        title="自定义字段组"
                        wrapClassName="card p-4 col-span-3"
                    />

                    {/* 功率校准曲线 */}
                    <div className="card p-4 col-span-3">
                        <div className="text-sm font-semibold mb-2">
                            功率校准曲线
                        </div>
                        <CalibChart
                            points={detail.calibration || []}
                            stdW={detail.capability?.stdW || 0}
                        />
                        <div className="text-xs text-slate-400 mt-2">
                            共 {(detail.calibration || []).length} 个校准点
                            <HelpTip
                                className="ml-1"
                                text="完整 % ↔ W 计算见「功率校准」子 tab。"
                            />
                        </div>
                    </div>

                    {/* 具备工艺类型 (2026-09-08): cap_type.deviceKeys 显式绑定的反向视图, 只读 + 跳转维护 */}
                    <DeviceCapTypesCard
                        deviceKey={detail.deviceKey}
                        deviceName={detail.name || detail.deviceKey}
                    />

                    {/* 加工小贴士 (2026-08-27): 设备自身的注意事项, 条目内容在小贴士库单源维护 */}
                    <div className="col-span-3">
                        <DeviceTipsCard device={detail} />
                    </div>

                    {/* 热校准 (按 mode × 材料类型) */}
                    <div className="card p-4 col-span-3 overflow-x-auto">
                        <div className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                            热校准
                            <HelpTip text="按 mode × 材料类型, 用于材料散热模型 τ/β。" />
                        </div>
                        <ThermalCalTable rows={detail.thermalCal || []} />
                    </div>
                </div>
            )}

            <YamlImportModal
                open={showImport}
                onClose={() => setShowImport(false)}
                onImported={() => {
                    // 重新拉一次当前设备
                    if (ctx.curDeviceKey) {
                        getDevice(ctx.curDeviceKey)
                            .then((d) => setDetail(d))
                            .catch(() => {});
                    }
                }}
            />
            <DeviceCreateModal
                open={showCreate}
                editing={editingDevice}
                onClose={() => setShowCreate(false)}
                onSaved={() => {
                    // 新建: 切到刚建的设备; 编辑: 刷新当前设备详情
                    if (ctx.curDeviceKey) {
                        getDevice(ctx.curDeviceKey)
                            .then((d) => setDetail(d))
                            .catch(() => {});
                    }
                }}
            />

            {mmEditOpen && detail && (
                <MModeRefModal
                    device={detail}
                    onClose={() => {
                        setMmEditOpen(false);
                        reloadMmRefs(); // 保存后回读 (admin 直改立即可见; 走审批的暂不变)
                    }}
                />
            )}

            <DeleteCascadeModal
                open={showDelete && !!detail}
                title="删除设备"
                targetLabel={
                    detail ? `${detail.deviceKey} ${detail.name || ''}` : ''
                }
                loadPreview={() =>
                    detail
                        ? fetchDeviceDeletePreview(detail.deviceKey)
                        : Promise.resolve([])
                }
                onClose={() => setShowDelete(false)}
                onConfirm={async (payload) => {
                    if (!detail) return;
                    const res = await deleteDevice(detail.deviceKey, payload);
                    if (alertApprovalIfPending(res.message)) {
                        // 走审批流: 设备还在, 本地 UI 不当作已删除
                        return;
                    }
                    dispatch(actions.device.setCur(''));
                    setDetail(null);
                }}
            />
        </div>
    );
}

export default DeviceProfile;
